/**
 * Sign-up effects — async side effects triggered by command actions.
 */

import type { EffectHandler } from '../../shared/flow-store';
import type { SignUpState, SignUpAction, SignUpDeps, SignUpCreateParams } from './types';
import { SignUpStatus } from '../../constants';
import type { SignUpProps } from '../../types/results';
import { extractErrorMessage } from '../../shared/extract-error-message';

type SignUpEffectHandler = EffectHandler<SignUpState, SignUpAction, SignUpDeps>;
type Dispatch = (a: SignUpAction) => void;

// ─── Effect handlers ─────────────────────────────────────────────────────────

async function submitRegistrationEffect(
  action: SignUpAction,
  _prevState: SignUpState,
  dispatch: Dispatch,
  deps: SignUpDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_REGISTRATION') return;
  await callSignUp(action.params, action.params.newsletter, dispatch, deps);
}

async function submitConsentEffect(
  action: SignUpAction,
  prevState: SignUpState,
  dispatch: Dispatch,
  deps: SignUpDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_CONSENT') return;
  if (prevState.step !== 'CONSENT_REQUIRED') return;

  // Capture consent first — failure returns the user to the consent form with an error.
  if (deps.config.captureConsent) {
    try {
      await deps.config.captureConsent({
        firstName: prevState.pendingProps.firstName,
        lastName: prevState.pendingProps.lastName,
        email: prevState.email,
        phone: prevState.pendingProps.homePhone ?? '',
      });
    } catch (err) {
      deps.observability.dispatch({ type: 'register_failure', error: err });
      dispatch({ type: 'CONSENT_FAIL', error: toMessage(err, 'Unable to save your consent. Please try again.') });
      return;
    }
  }

  const params = { ...prevState.pendingProps, newsletter: action.newsletterConsent };
  await callSignUp(params, action.newsletterConsent, dispatch, deps);
}

async function submitVerificationEffect(
  action: SignUpAction,
  prevState: SignUpState,
  dispatch: Dispatch,
  deps: SignUpDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_VERIFICATION') return;
  if (prevState.step !== 'AWAITING_VERIFICATION') return;

  try {
    const result = await deps.service.confirmSignUp(prevState.email, action.code);

    if ('status' in result && result.status === SignUpStatus.RequiresEmailVerification) {
      dispatch({ type: 'VERIFICATION_FAIL', error: 'Confirmation failed. Please try again.' });
      return;
    }

    if ('user' in result && result.user) {
      deps.session.completeAuth(result.user);
      deps.observability.dispatch({ type: 'register_success' });
      deps.eventBus.emit({
        type: 'sign_up_complete',
        user: result.user,
        newsletterConsent: prevState.pendingProps?.newsletter,
      });
      dispatch({ type: 'VERIFICATION_OK', user: result.user });
      return;
    }

    dispatch({ type: 'VERIFICATION_NEEDS_SIGN_IN' });
  } catch (err) {
    deps.observability.dispatch({ type: 'register_failure', error: err });
    dispatch({ type: 'VERIFICATION_FAIL', error: toMessage(err, 'Verification failed.') });
  }
}

async function submitPhoneEffect(
  action: SignUpAction,
  _prevState: SignUpState,
  dispatch: Dispatch,
  deps: SignUpDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_PHONE') return;

  try {
    const userId = deps.session.getSnapshot().user?.id ?? '';
    const result = await deps.service.submitPhone(userId, action.phone);
    if (result && 'user' in result && result.user) {
      deps.session.completeAuth(result.user);
      dispatch({ type: 'PHONE_OK', user: result.user });
    } else {
      deps.session.clearPendingAction();
      dispatch({ type: 'PHONE_OK', user: null });
    }
  } catch (err) {
    deps.observability.dispatch({ type: 'register_failure', error: err });
    dispatch({ type: 'PHONE_FAIL', error: toMessage(err) });
  }
}

async function resendCodeEffect(
  action: SignUpAction,
  prevState: SignUpState,
  dispatch: Dispatch,
  deps: SignUpDeps,
): Promise<void> {
  if (action.type !== 'RESEND_CODE') return;
  const email = 'email' in prevState ? (prevState.email as string) : null;
  if (!email) return;

  try {
    await deps.service.resendSignUpCode(email);
    dispatch({ type: 'RESEND_CODE_OK' });
  } catch (err) {
    deps.observability.dispatch({ type: 'register_failure', error: err });
    dispatch({ type: 'RESEND_CODE_FAIL', error: toMessage(err, 'Failed to resend code.') });
  }
}

export const signUpEffects: Partial<Record<SignUpAction['type'], SignUpEffectHandler>> = {
  SUBMIT_REGISTRATION: submitRegistrationEffect,
  SUBMIT_CONSENT: submitConsentEffect,
  SUBMIT_VERIFICATION: submitVerificationEffect,
  SUBMIT_PHONE: submitPhoneEffect,
  RESEND_CODE: resendCodeEffect,
};

// ─── Sub-handlers ─────────────────────────────────────────────────────────────

/**
 * Calls `service.signUp` and dispatches the appropriate result action.
 * Shared by the direct registration path and the post-consent path.
 *
 * @param newsletterConsent - The newsletter opt-in value from the form, forwarded to the event.
 */
async function callSignUp(
  params: SignUpCreateParams,
  newsletterConsent: boolean | undefined,
  dispatch: Dispatch,
  deps: SignUpDeps,
): Promise<void> {
  try {
    const result = await deps.service.signUp(toSignUpProps(params));

    if (result.status === SignUpStatus.RequiresEmailVerification) {
      dispatch({ type: 'SIGN_UP_NEEDS_VERIFICATION' });
      return;
    }
    if (result.status === SignUpStatus.Confirmed && result.user) {
      deps.session.completeAuth(result.user);
      deps.observability.dispatch({ type: 'register_success' });
      deps.eventBus.emit({
        type: 'sign_up_complete',
        user: result.user,
        newsletterConsent,
      });
      dispatch({ type: 'SIGN_UP_OK', user: result.user });
      return;
    }
    dispatch({ type: 'SIGN_UP_INCOMPLETE' });
  } catch (err) {
    deps.observability.dispatch({ type: 'register_failure', error: err });
    dispatch({ type: 'SIGN_UP_FAIL', error: toMessage(err, 'Something went wrong. Please try again.') });
  }
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function toSignUpProps(params: SignUpCreateParams): SignUpProps {
  return {
    email: params.emailAddress,
    password: params.password,
    firstName: params.firstName,
    lastName: params.lastName,
    homePhone: params.homePhone,
    newsletter: params.newsletter,
    privacyTerms: params.privacyTerms,
    signupUrl: params.signupUrl,
    userCountry: params.userCountry,
    theme: params.theme,
  };
}

export function toMessage(err: unknown, fallback = 'Something went wrong.'): string {
  return extractErrorMessage(err, fallback);
}
